import React from 'react';
import { useSelector } from 'react-redux';
import { Pie } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend
} from 'chart.js';

ChartJS.register(
  ArcElement,
  Tooltip,
  Legend
);

const FundsChart = ({ selectedFrequency }) => {
  const fundStats = useSelector((s) => s?.adminReducer?.dashboardFundStats);

  const getFreqKey = (freq) => {
    const f = String(freq || '').toLowerCase();
    if (f === 'today' || f === 'day') return 'today';
    if (f === 'weekly' || f === 'week') return 'weeklyTotal';
    if (f === 'monthly' || f === 'month') return 'monthlyTotal';
    if (f === 'yearly' || f === 'year') return 'yearly';
    return 'total';
  };

  const pickAmount = (obj, paths) => {
    for (const path of paths) {
      const value = path.split('.').reduce((acc, key) => acc?.[key], obj);
      if (typeof value === 'number') return value;
    }
    return 0;
  };

  const formatCurrency = (cents) => {
    const dollars = (Number(cents) || 0) / 100;
    if (Math.abs(dollars) >= 1000000) {
      return `$${(dollars / 1000000).toFixed(2)}M`;
    }
    if (Math.abs(dollars) >= 1000) {
      return `$${dollars.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    }
    return `$${dollars.toFixed(2)}`;
  };

  const freqKey = getFreqKey(selectedFrequency);
  const currentStats = fundStats?.[freqKey] || fundStats || {};

  const centsIn = pickAmount(currentStats, ['fundraiser_in', 'fundsIn', 'in']);
  const centsOut = pickAmount(currentStats, ['fundraiser_out', 'fundsOut', 'out']);

  const dollarsIn = centsIn / 100;
  const dollarsOut = centsOut / 100;
  const totalDollars = dollarsIn + dollarsOut;

  const isZero = totalDollars === 0;
  const displayData = isZero
    ? [1, 1] // show ring colors even when values are zero
    : [dollarsIn, dollarsOut];

  const data = {
    labels: ['Funds In', 'Funds Out'],
    datasets: [
      {
        data: displayData,
        backgroundColor: [
          '#91CC75',
          '#EE6666',
        ],
        borderWidth: 0,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            if (isZero) {
              return `${context.label}: $0.00`;
            }
            const dollars = context.raw;
            if (Math.abs(dollars) >= 1000000) {
              return `${context.label}: $${(dollars / 1000000).toFixed(2)}M`;
            }
            return `${context.label}: $${dollars.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
          }
        }
      }
    },
    cutout: '60%',
  };

  const legendItems = [
    { label: 'Funds In:', value: formatCurrency(centsIn), color: '#91CC75' },
    { label: 'Funds Out:', value: formatCurrency(centsOut), color: '#EE6666' },
  ];

  return (
    <div className="summary w-full p-4 h-[470px] flex flex-col bg-white rounded-lg">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-[20px] font-poppins font-[600] text-[#303030] leading-none">Funds In & Funds Out</h2>
      </div>
      <div className="flex-1 min-h-0 flex flex-col sm:flex-row">
        {/* Left side - Chart */}
        <div className="w-full sm:w-1/2 relative h-[200px] sm:h-auto">
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center">
              <div className="text-[16px] font-[600] text-[#303030]">Total</div>
              <div className="text-[18px] font-[700] text-[#303030] whitespace-nowrap px-1">
                {formatCurrency(centsIn + centsOut)}
              </div>
            </div>
          </div>
          <Pie data={data} options={options} />
        </div>
        
        {/* Right side - Custom Legend */}
        <div className="w-full sm:w-1/2 flex flex-col justify-center pl-0 sm:pl-8 mt-2 sm:mt-0">
          <div className="flex flex-col gap-3 sm:gap-8 mx-auto sm:mx-0">
            {legendItems.map((item, index) => (
              <div key={index} className="flex items-center gap-3">
                <div className="w-8 h-4 rounded-sm" style={{ backgroundColor: item.color }}></div>
                <span className="text-[14px] font-poppins font-[600] text-[#303030]">{item.label}</span>
                <span className="text-[14px] font-poppins text-[#8C8C8C]">{item.value}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FundsChart; 
